import {
  Button,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { limeFocus } from "./fieldStyles";

const MIN_YEAR = 1990;
const MAX_YEAR = 2100;

/**
 * Adds a reporting year — which creates one question per workstream, not one question.
 *
 * A pill button in the platform's primary-action style (Data Collection's "Assign new task": lime, white
 * text, fully rounded) that opens a small picker for the year to create. The year to ADD lives here, not
 * beside the page's filters: it is not a filter, and a number box next to the year filter read as one.
 *
 * Hidden rather than disabled for a viewer: this app answers an authority denial with 401, so a control that
 * looks usable and then fails opaquely is worse than no control.
 */
export default function AddYearControl({ onAddYear, isAdding }) {
  const [draftYear, setDraftYear] = useState(new Date().getFullYear());
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isValid = draftYear && draftYear >= MIN_YEAR && draftYear <= MAX_YEAR;

  const submit = () => {
    if (!isValid) return;
    onAddYear(draftYear);
    onClose();
  };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end">
      <PopoverTrigger>
        <Button
          size="sm"
          bg="limeShade.600"
          color="white"
          borderRadius="full"
          _hover={{ bg: "limeShade.700" }}
          _active={{ bg: "limeShade.700" }}
          leftIcon={<Plus size={14} weight="bold" />}
          isLoading={isAdding}
          flexShrink={0}
        >
          Add year
        </Button>
      </PopoverTrigger>
      <PopoverContent w="240px" borderRadius="lg">
        <PopoverArrow />
        <PopoverBody p={3}>
          <Text fontSize="12px" color="gray.500" mb={2}>
            Reporting year to add
          </Text>
          <HStack spacing={2}>
            <NumberInput
              size="sm"
              flex="1"
              min={MIN_YEAR}
              max={MAX_YEAR}
              value={draftYear}
              onChange={(_, valueAsNumber) =>
                setDraftYear(Number.isNaN(valueAsNumber) ? "" : valueAsNumber)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
            >
              <NumberInputField {...limeFocus} />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Button
              size="sm"
              bg="limeShade.600"
              color="white"
              borderRadius="full"
              _hover={{ bg: "limeShade.700" }}
              isDisabled={!isValid}
              onClick={submit}
            >
              Add
            </Button>
          </HStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
